import { useSearchParams } from 'react-router-dom'
import { useOrgContext } from './context/organization/org-context'
import { useCurrentUser } from './use-current-user'
import { useUserFarms } from './use-user-farms'

/**
 * Hook to resolve farmId from multiple sources
 * Priority: URL params > OrgContext > Database (user record) > Default farm
 *
 * @returns {Object} { farmId, loading, source }
 */
export const useFarmId = () => {
  const [ searchParams ] = useSearchParams()
  const { lastSelectedFarmId } = useOrgContext()
  const { currentUser, loading: userLoading } = useCurrentUser()
  const { userFarms, loading: farmLoading } = useUserFarms({ user: currentUser })

  const urlFarmId = searchParams.get('farmId')

  // Priority 1: URL params (highest priority)
  if (urlFarmId) {
    return {
      farmId: urlFarmId,
      loading: false,
      source: 'url'
    }
  }

  // Still loading user/farm data
  if (userLoading || farmLoading) {
    return {
      farmId: null,
      loading: true,
      source: null
    }
  }

  // Priority 2: OrgContext (last selected during current session)
  if (lastSelectedFarmId) {
    return {
      farmId: lastSelectedFarmId,
      loading: false,
      source: 'context'
    }
  }

  // Priority 3: Database (persisted across logins)
  const dbFarmId = currentUser?.last_selected_farm_id
  if (dbFarmId && userFarms?.some(farm => farm.id === dbFarmId)) {
    return {
      farmId: dbFarmId,
      loading: false,
      source: 'database'
    }
  }

  // Priority 4: Default farm (first in user's farm list)
  if (userFarms && userFarms[0]?.id) {
    return {
      farmId: userFarms[0].id,
      loading: false,
      source: 'default'
    }
  }

  // No farm available
  return {
    farmId: null,
    loading: false,
    source: null
  }
}
